import {
  createFileRoute,
  Outlet,
  redirect,
  useChildMatches,
  useNavigate,
} from '@tanstack/react-router'
import { type LeaveRequestsSearch, leaveRequestsSearchSchema } from '@/features/hr-leave/search'
import { TimeOffList } from '@/features/hr-leave/time-off-list'
import { PermissionTab } from '@/features/hr-permissions/permission-tab'
import { hasGrant } from '@/lib/me'

/**
 * Route shell for the manager-facing Leave Requests screen.
 *
 * **The path is `/hr/leave`, not `/hr/time-off`.** The plan names the files
 * `time-off*.tsx`, but slice 0 wrote the module rail — which no slice 1–7 may
 * edit — and its HR entry points at `/hr/leave` under the label "Time off". A
 * route at the plan's path would leave the rail linking to a 404. The feature
 * directory and API helper retain their historical Time Off names, while the
 * visible screen is intentionally focused on leave requests.
 *
 * The grant guard is presentation only. The API enforces `leave.view` on every
 * endpoint, so a stale grant still 403s server-side; this keeps a user without
 * the grant out of a screen they cannot use.
 *
 * Per the route-shell rule in `apps/company/AGENTS.md`, this file is a route
 * definition and a render of a feature. The list and decision dialog live in
 * `src/features/hr-leave/`; employee request forms remain in the ESS feature.
 *
 * ## Why this renders an Outlet
 *
 * `leave.$requestId.tsx` is a CHILD of this route in flat file routing, so without an
 * Outlet it never mounts and the detail URL silently rendered this list
 * instead. Slice 8 found the same shape in three places; see
 * `employees.$employeeId.tsx` for the full note and the tidier fix.
 */
export const Route = createFileRoute('/_authenticated/hr/leave')({
  validateSearch: leaveRequestsSearchSchema,
  beforeLoad: ({ context }) => {
    if (!hasGrant(context.queryClient, 'leave.view')) {
      throw redirect({ to: '/' })
    }
  },
  component: TimeOffRoute,
})

function TimeOffRoute() {
  const children = useChildMatches()
  const navigate = useNavigate()
  const search = Route.useSearch()

  if (children.length > 0) {
    return <Outlet />
  }

  return (
    <TimeOffList
      search={search}
      onSearchChange={(patch: Partial<LeaveRequestsSearch>) =>
        void navigate({
          to: '/hr/leave',
          search: { ...search, ...patch },
          replace: true,
        })
      }
      hourlyPanel={<PermissionTab />}
    />
  )
}
